import type { ReactNode } from 'react'

/*
 * A card is a board panel: squared, hairline-bordered, sitting slightly proud
 * of the housing. No large radii and no drop shadows — depth comes from the
 * surface ramp (canvas → surface → elevated), not from blur.
 */
export function Card({ className = '', children }: { className?: string; children: ReactNode }) {
  return (
    <div className={`overflow-hidden rounded border border-line bg-surface ${className}`}>
      {children}
    </div>
  )
}

export function CardHeader({
  title,
  description,
  action,
}: {
  title: ReactNode
  description?: ReactNode
  action?: ReactNode
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 border-b border-line px-4 py-3 sm:px-5">
      <div className="min-w-0">
        <h2 className="signage text-[10px] text-muted">{title}</h2>
        {description ? <p className="mt-1.5 text-sm text-muted">{description}</p> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  )
}

/** `flush` drops the padding for full-bleed lists — never fight it with px-0. */
export function CardBody({
  className = '',
  flush = false,
  children,
}: {
  className?: string
  flush?: boolean
  children: ReactNode
}) {
  return <div className={`${flush ? '' : 'px-4 py-4 sm:px-5'} ${className}`}>{children}</div>
}

/**
 * Page masthead.
 *
 * The amber lead-in rule under the title is the board's column-header device:
 * a short signal segment, then neutral hairline. It marks where a section
 * begins without needing an eyebrow label stacked above every heading.
 */
export function PageHeader({
  title,
  description,
  action,
}: {
  title: string
  description?: ReactNode
  action?: ReactNode
}) {
  return (
    <header className="mb-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h1 className="signage text-lg text-ink sm:text-xl">{title}</h1>
          {description ? <p className="mt-2 max-w-prose text-sm text-muted">{description}</p> : null}
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
      <div aria-hidden className="rule-signal mt-3 h-px w-full" />
    </header>
  )
}
